import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Clock, TrendingUp, Workflow } from "lucide-react";
import { Reveal, Magnetic, scrollTo } from "../lib/motion";
import { Kicker } from "./MidSections";

const MANIFESTO = [
  { n: "01", t: "We don't sell software.", d: "We deliver outcomes — intelligent systems engineered around how your business actually works." },
  { n: "02", t: "We build AI employees.", d: "Autonomous agents that own entire functions, from marketing to finance, around the clock." },
  { n: "03", t: "We automate repetitive work.", d: "Every manual, low-leverage task becomes a workflow that runs itself, flawlessly." },
  { n: "04", t: "We reduce operational costs.", d: "Scale output without scaling headcount, overhead, or complexity." },
  { n: "05", t: "We improve customer experience.", d: "Instant, consistent, human-quality interactions at any volume." },
  { n: "06", t: "We help businesses scale.", d: "Infrastructure that compounds — the more it runs, the smarter it gets." },
];

export function WhyUs() {
  return (
    <section id="why" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="why-section">
      <div className="mb-16 max-w-2xl">
        <Kicker id="why-kicker">Why SÛRSWA AI</Kicker>
        <Reveal>
          <h2 className="font-display text-4xl font-extrabold tracking-normal md:text-6xl">
            Not a vendor. <span className="text-[#8a99a8]">An operating system for growth.</span>
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 gap-x-16 gap-y-12 md:grid-cols-2">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={(i % 2) * 0.08}>
            <div className="border-t border-white/10 pt-6" data-testid={`manifesto-${i}`}>
              <span className="font-mono text-sm text-[#b8c0cb]">{m.n}</span>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-normal md:text-3xl">{m.t}</h3>
              <p className="mt-3 max-w-md text-base leading-relaxed text-[#8a99a8]">{m.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const HOW = [
  { t: "Business Growth Assessment", d: "We map your operations and surface where time and money leak." },
  { t: "AI Opportunity Audit", d: "We identify the highest-leverage processes to automate first." },
  { t: "Implementation Roadmap", d: "A phased, prioritised plan with clear milestones and ROI." },
  { t: "Build & Launch", d: "We design, build and deploy your AI employees into production." },
  { t: "Continuous Optimisation", d: "We monitor, refine and expand as your business evolves." },
];

export function HowItWorks() {
  return (
    <section className="border-y border-white/5 bg-[#0b0c0d] py-28 md:py-36" data-testid="how-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-4">
          <Kicker id="how-kicker">How it works</Kicker>
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold tracking-normal md:text-5xl">
              Five steps to an autonomous business.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          {HOW.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.06}>
              <div className="group flex gap-6 border-t border-white/10 py-8" data-testid={`how-step-${i}`}>
                <span className="font-mono text-sm text-[#8a99a8]">Step {i + 1}</span>
                <div>
                  <h3 className="font-display text-2xl font-bold tracking-normal transition-colors duration-300 group-hover:text-[#b8c0cb]">{s.t}</h3>
                  <p className="mt-2 max-w-lg text-base leading-relaxed text-[#8a99a8]">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AssessmentCTA() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="assessment-section">
      <Reveal>
        <div className="glass relative overflow-hidden rounded-3xl p-10 md:p-16">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#b8c0cb]/15 blur-[100px]" />
          <div className="relative max-w-2xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-[#b8c0cb]">
              <Sparkles className="h-3.5 w-3.5" /> Complimentary
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-normal md:text-6xl">
              Discover how AI can transform your business.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#8a99a8] md:text-lg">
              Complete our Business Growth Assessment to identify opportunities to improve
              efficiency, reduce manual work and accelerate growth through AI and automation.
            </p>
            <Magnetic>
              <a
                href="https://tally.so/r/EkGvEL"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="assessment-cta-button"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#b8c0cb] px-7 py-4 text-sm font-semibold text-[#070708] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                Start Growth Assessment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Magnetic>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

const OUTCOMES = [
  { icon: Clock, label: "Hours saved", t: "Reclaim the week", d: "AI employees absorb the repetitive, manual work that quietly consumes your team's time — running every hour, without breaks." },
  { icon: TrendingUp, label: "Revenue increased", t: "Compound the pipeline", d: "Always-on prospecting, instant follow-up and personalised outreach that turns attention into revenue while you sleep." },
  { icon: Workflow, label: "Processes automated", t: "Systemise operations", d: "Manual workflows become self-running systems — consistent, auditable and engineered to scale with you." },
];

export function CaseStudies() {
  return (
    <section id="cases" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="cases-section">
      <div className="mb-16 max-w-2xl">
        <Kicker id="cases-kicker">Outcomes</Kicker>
        <Reveal>
          <h2 className="font-display text-4xl font-semibold tracking-normal md:text-6xl">
            The outcomes we engineer.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#868d97]">
            Every system we build is measured against three levers of growth — the ones that
            move a business forward from the day it goes live.
          </p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {OUTCOMES.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.08}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="lux-card group relative h-full overflow-hidden rounded-2xl p-8"
              data-testid={`case-card-${i}`}
            >
              <div className="pointer-events-none absolute -left-16 -bottom-16 h-40 w-40 rounded-full bg-white/0 blur-3xl transition-all duration-500 group-hover:bg-white/[0.06]" />
              <div className="mb-8 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                <c.icon className="h-5 w-5 text-[#b8c0cb]" />
              </div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#868d97]">{c.label}</span>
              <h3 className="mt-2 font-display text-2xl font-semibold tracking-normal text-metal">{c.t}</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#868d97]">{c.d}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
